// src/common/theme/palette.js
// ------------------------------------------------------------------
// Paleta Premium consolidada (dark/light) com contraste refinado
// Harmonização entre botões, tabelas, fundo e identidade visual
// Mantém compatibilidade com Revenda/Server
// ------------------------------------------------------------------

import { grey, green, red, orange, blue } from "@mui/material/colors";

// ------------------------------------------------------------------
// Validação de cor HEX
// ------------------------------------------------------------------
const validatedColor = (color) =>
  /^#([0-9A-Fa-f]{3}){1,2}$/.test(color) ? color : null;

// ------------------------------------------------------------------
// Escalas auxiliares (cinzas + institucional)
// ------------------------------------------------------------------
const tailWindGray = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
};

const brandPrimaryScale = {
  50: "#FFF5F2",
  100: "#FFF1EE",
  200: "#FFE4DE",
  300: "#FFD5CC",
  400: "#FFBCAD",
  500: "#FE795D",
  600: "#EF562F",
  700: "#EB4F27",
  800: "#CC4522",
  900: "#A5371B",
};

// ------------------------------------------------------------------
// Bases refinadas
// ------------------------------------------------------------------
const BASE_LIGHT = {
  primary: {
    main: "#EF562F",
    light: "#FFF1EE",
    dark: "#C44523",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#F87137",
    contrastText: "#FFFFFF",
  },
  background: {
    default: "#FAFAFA",
    paper: "#FFFFFF",
  },
  text: {
    primary: "#1E1E1E",
    secondary: "#4E4E4E",
  },
};

const BASE_DARK = {
  primary: {
    main: "#F87137",
    light: "#FF9E6B",
    dark: "#C44523",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#EF562F",
    contrastText: "#FFFFFF",
  },
  background: {
    default: "#1C1C1C",
    paper: "#1F1F1F",
  },
  text: {
    primary: "#FFFFFF",
    secondary: "#B0B0B0",
  },
};

// ------------------------------------------------------------------
// Export principal
// ------------------------------------------------------------------
export default (server, revenda, darkMode) => {
  const tenant = revenda || server?.revenda || server?.attributes?.revenda || {};

  const primaryColor = validatedColor(
    tenant?.colors?.primary
      || tenant?.color1
      || server?.attributes?.colorPrimary
  );
  const secondaryColor = validatedColor(
    tenant?.colors?.secondary
      || tenant?.color2
      || server?.attributes?.colorSecondary
  );
  const successColor = validatedColor(
    tenant?.colors?.success
      || tenant?.color3
      || server?.attributes?.colorSuccess
  );

  const base = darkMode ? BASE_DARK : BASE_LIGHT;

  const primary = {
    ...base.primary,
    ...(primaryColor ? { main: primaryColor } : {}),
  };

  const secondary = {
    ...base.secondary,
    ...(secondaryColor ? { main: secondaryColor } : {}),
  };

  const success = {
    main: successColor || green[600],
    light: green[400],
    dark: green[800],
    contrastText: "#fff",
  };

  const error = {
    main: red[600],
    light: red[400],
    dark: red[800],
    contrastText: "#fff",
  };

  const warning = {
    main: orange[600],
    light: orange[400],
    dark: orange[800],
    contrastText: "#fff",
  };

  const info = {
    main: blue[600],
    light: blue[400],
    dark: blue[800],
    contrastText: "#fff",
  };

  // Cores padrão para export (podem ser sobrescritas por tenant)
  const defaults = {
    csv: '#F97316',  csvHover: '#FB923C',
    excel: '#009688', excelHover: '#00796B',
    pdf: '#E53935',   pdfHover: '#C62828',
  };

  // 🔧 CORREÇÃO: construir a paleta de export como variável local
  const exportPalette = {
    csv:       tenant?.colors?.csv       || defaults.csv,
    csvHover:  tenant?.colors?.csvHover  || defaults.csvHover,
    excel:     tenant?.colors?.excel     || defaults.excel,
    excelHover:tenant?.colors?.excelHover|| defaults.excelHover,
    pdf:       tenant?.colors?.pdf       || defaults.pdf,
    pdfHover:  tenant?.colors?.pdfHover  || defaults.pdfHover,
  };

  return {
    mode: darkMode ? "dark" : "light",
    primary,
    secondary,
    success,
    error,
    warning,
    info,
    background: base.background,
    text: base.text,

    // --------------------------------------------------------------
    // Paletas auxiliares
    // --------------------------------------------------------------
    neutral: {
      main: grey[500],
      icon3d: brandPrimaryScale[400],
    },
    geometry: { main: "#3bb2d0" },
    alwaysDark: { main: grey[900] },

    // --------------------------------------------------------------
    // Customizações específicas
    // --------------------------------------------------------------
    // customizações usadas no cabeçalho/hover da tabela
    custom: {
      table: {
        headerBg:    darkMode ? "#242424" : "#FFF1EE",
        headerBorder:darkMode ? "#333333" : "#FFE4DE",
        rowHover:    darkMode ? "#2E2E2E" : "#FAFAFA",
        headerText:  darkMode ? "#FFFFFF" : "#1E1E1E",
      },
      // (mantive seu bloco 'button' se você o usa em outras partes)
      button: {
        add:  { light: "#2E8B57", dark: "#4CAF50", textLight: "#FFFFFF", textDark: "#FFFFFF" },
        csv:  { light: "#EF562F", dark: "#F57C00", textLight: "#FFFFFF", textDark: "#FFFFFF" },
        xlsx: { light: "#009688", dark: "#26A69A", textLight: "#FFFFFF", textDark: "#1C1C1C" },
        pdf:  { light: "#D32F2F", dark: "#E57373", textLight: "#FFFFFF", textDark: "#1C1C1C" },
      },
    },

    // 🔌 exposto para os Botões CSV/Excel/PDF nos overrides
    export: exportPalette,
  };
};
